import React, { useState } from 'react';
import axios from 'axios';
import { Form, Row, Col, Button, ButtonGroup, ToggleButton, ToggleButtonGroup } from '../../ui-kit';
import GenericAvailabilityCodeSelector from '../../general/AvailabilityCodeSelector';
import PeriodSelector from '../../../utils/forms/PeriodSelector';
import { roundTimeDown } from '../../../utils/time';
import Loader from '../../general/Loader';
import ScheduleWarningsPopoverConnected from '../warnings/ScheduleWarningsPopoverConnected';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { isEmpty } from 'lodash';
import toSentence from '../../../utils/toSentence';
import moment from 'moment';

const AvailabilityCodeSelector = ({ availabilityCodes, availabilityCodesLoaded, updateAvailabilityCode }) => {
  if (isEmpty(availabilityCodes)) return null;

  return (
    <Form.Group as={Row} className="mb-3">
      <Form.Label column sm={3}>
        {I18n.t('activerecord.attributes.schedule/availability_code.short_model_name')}
      </Form.Label>
      <Col>
        <GenericAvailabilityCodeSelector
          loading={!availabilityCodesLoaded}
          availabilityCodes={availabilityCodes}
          onChange={updateAvailabilityCode}
          multi={false}
        />
      </Col>
    </Form.Group>
  );
};

const SimpleGroupSelectionFields = ({ currentUserPlannableStationMemberships, saving, updateExceptionSchedule }) => (
  <>
    <ButtonGroup className="w-100 mb-3">
      <Button className="w-50" variant="success" disabled={saving} onClick={() => updateExceptionSchedule(true)}>
        {I18n.t('check_in')}
      </Button>
      <Button className="w-50" variant="danger" disabled={saving} onClick={() => updateExceptionSchedule(false)}>
        {I18n.t('check_out')}
      </Button>
    </ButtonGroup>
    <p className="text-center">
      <b>{I18n.t('quick_availability_form.applies_to')}</b>{' '}
      {toSentence(currentUserPlannableStationMemberships.map(m => m.group.name))}
    </p>
  </>
);

const DetailedGroupSelectionFields = ({
  currentUserPlannableStationMemberships,
  exceptionParams,
  setExceptionParams,
  saving,
  updateExceptionSchedule
}) => {
  const setMembershipsToUpdate = (membershipId, status) =>
    setExceptionParams({
      ...exceptionParams,
      membership_availability: {
        ...exceptionParams.membership_availability,
        [membershipId]: status
      }
    });

  const valid = !isEmpty(exceptionParams.membership_availability);

  return (
    <>
      {currentUserPlannableStationMemberships.map(membership => (
        <Row key={membership.id} className="mb-3">
          <Col>{membership.group.name}</Col>
          <Col className="d-flex justify-content-end">
            <ToggleButtonGroup
              type="radio"
              name={`availability_for_${membership.id}`}
              className="mb-2"
              onChange={e => setMembershipsToUpdate(membership.id, e)}
            >
              <ToggleButton
                id={`${membership.id}-on-button`}
                variant={
                  exceptionParams.membership_availability[membership.id] === true ? 'success' : 'outline-success'
                }
                value={true}
              >
                {I18n.t('check_in_abbr')}
              </ToggleButton>
              <ToggleButton
                id={`${membership.id}-off-button`}
                variant={exceptionParams.membership_availability[membership.id] == false ? 'danger' : 'outline-danger'}
                value={false}
              >
                {I18n.t('check_out_abbr')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Col>
        </Row>
      ))}
      <Button className="w-100" variant="primary" disabled={saving || !valid} onClick={() => updateExceptionSchedule()}>
        {I18n.t('save')}
      </Button>
    </>
  );
};

const defaultValues = () => ({
  start_time: roundTimeDown(moment()).format(),
  end_time: roundTimeDown(moment().add(2, 'hours')).format(),
  channel: 'desktop',
  comments: '',
  membership_availability: {}
});

const AvailabilityChangeForm = ({
  availabilityCodes,
  availabilityCodesLoaded,
  currentUser,
  currentUserPlannableStationMemberships
}) => {
  const [showDetailedForm, setShowDetailedForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exceptionParams, setExceptionParams] = useState(defaultValues());
  const [currentExceptionParams, setCurrentExceptionParams] = useState();

  const updateAvailabilityCode = availabilityCodes => {
    setExceptionParams({ ...exceptionParams, availability_code_id: availabilityCodes[0] });
  };

  const calculateParams = value => {
    if (showDetailedForm) return exceptionParams;

    // we're in the simple form
    const startTime = moment();
    const endTime = startTime
      .clone()
      .hour(moment(exceptionParams.end_time).hour())
      .minute(moment(exceptionParams.end_time).minute())
      .second(0);
    if (endTime.isBefore(startTime)) endTime.add(1, 'day');

    // remove membership_availability from exceptionParams, this is needed if
    // we are switching from detailed to simple view since we only care about
    // the 'available'' param
    let { membership_availability, ...params } = exceptionParams;
    return {
      ...params,
      start_time: startTime.format(),
      end_time: endTime.format(),
      available: value
    };
  };

  const updateExceptionSchedule = async value => {
    const params = calculateParams(value);

    // register the params in case of schedule warnings so we can retry
    setCurrentExceptionParams(params);

    return await performAPICall(params, false);
  };

  const retryIgnoreWarnings = async () => await performAPICall(currentExceptionParams, true);

  const performAPICall = async (params, ignore_schedule_warnings) => {
    setSaving(true);

    params = { ...params, ignore_schedule_warnings };
    const url = `/api/v2/users/${currentUser.id}/schedule_exceptions`;
    try {
      await axios.post(url, params);
      toast.info(I18n.t('changes_saved_successfully'), {
        position: 'top-center',
        autoClose: 3000,
        closeOnClick: false
      });
      toast();
      setExceptionParams(defaultValues());
      setCurrentExceptionParams(null);
      setShowDetailedForm(false);
    } finally {
      setSaving(false);
    }
  };

  const updateDatetimeFields = e =>
    setExceptionParams({ ...exceptionParams, start_time: e.start_time, end_time: e.end_time });

  const handleComments = e => setExceptionParams({ ...exceptionParams, comments: e.target.value });

  if (!availabilityCodesLoaded || !currentUser) return <Loader />;

  if (currentUser && isEmpty(currentUserPlannableStationMemberships))
    return (
      <h5 className="text-center p-2">
        <i>{I18n.t('dashboards.widget_archetypes.quick_availability_change.no_schedule')}</i>
      </h5>
    );

  const CurrentGroupSelectionFields = showDetailedForm ? DetailedGroupSelectionFields : SimpleGroupSelectionFields;

  return (
    <>
      <ScheduleWarningsPopoverConnected handleUpdated={retryIgnoreWarnings} handleDismissed={() => setSaving(false)} />
      <Form className="p-3">
        <div className="mb-2">
          <PeriodSelector
            startTime={exceptionParams.start_time}
            endTime={exceptionParams.end_time}
            onChange={updateDatetimeFields}
            minutesInterval={15}
            horizontal
          />
        </div>
        <AvailabilityCodeSelector
          availabilityCodes={availabilityCodes}
          availabilityCodesLoaded={availabilityCodesLoaded}
          updateAvailabilityCode={updateAvailabilityCode}
        />
        <Form.Group as={Row} className="mb-3">
          <Col>
            <Form.Control
              as="textarea"
              placeholder={I18n.t('activerecord.attributes.schedule/schedule_exception.comments')}
              onChange={handleComments}
            />
          </Col>
        </Form.Group>
        {currentUserPlannableStationMemberships.length > 1 && (
          <Form.Group as={Row} className="mb-3">
            <Col auto>
              <Form.Check
                className="float-end"
                type="switch"
                checked={showDetailedForm}
                id={'showDetailedForm'}
                onChange={e => setShowDetailedForm(e.target.checked)}
                label={I18n.t('dashboards.widget_archetypes.quick_availability_change.detailed_group_selection')}
              />
            </Col>
          </Form.Group>
        )}
        <CurrentGroupSelectionFields
          currentUserPlannableStationMemberships={currentUserPlannableStationMemberships}
          exceptionParams={exceptionParams}
          saving={saving}
          setExceptionParams={setExceptionParams}
          updateExceptionSchedule={updateExceptionSchedule}
        />
      </Form>
    </>
  );
};

export default AvailabilityChangeForm;
